import { api } from '@/shared/services/baseApiClient';
import urljoin from 'url-join';
import WorkspaceViewModel from './models/workspaceViewModel';
import { config } from '@/config/config';
import { UpdateWorkspaceViewModel } from './models/request/updateWorkspaceViewModel';
import { InviteUserViewModel } from '@/workspaces/models/request/inviteUserViewModel';
import { UserViewModel } from '@/workspaces/models/userViewModel';
import { RevokeUserViewModel } from '@/workspaces/models/request/revokeUserViewModel';
import { ValidateInviteTokenViewModel } from '@/workspaces/models/response/validateInviteTokenViewModel';
import { RegisterUserViewModel } from '@/workspaces/models/request/registerUserViewModel';
import FeatureFlagViewModel from './models/featureFlagViewModel';
import AddWorkspaceViewModel from '@/workspaces/models/request/addWorkspaceViewModel';

class WorkspacesApi {
  private version = 'v1';

  public loadAll(asAdmin: boolean = false): Promise<WorkspaceViewModel[]> {
    const parameters = asAdmin ? '?admin=true' : '';
    const url = urljoin(config().workspacesHost, this.version, 'workspaces', parameters);
    return api.send('GET', url);
  }

  public getById(id: string): Promise<WorkspaceViewModel> {
    const url = urljoin(config().workspacesHost, this.version, 'workspaces', id);
    return api.send('GET', url);
  }

  public createWorkspace(request: AddWorkspaceViewModel): Promise<WorkspaceViewModel> {
    const url = urljoin(config().workspacesHost, this.version, 'workspaces');
    return api.send('POST', url, request);
  }

  public async update(workspaceId: string, request: UpdateWorkspaceViewModel): Promise<WorkspaceViewModel> {
    const url = urljoin(config().workspacesHost, this.version, 'workspaces', workspaceId);
    return await api.send('PUT', url, request);
  }

  public inviteUsers(emails: string[], workspaceId: string) {
    const inviteUserModel: InviteUserViewModel = { emails, workspaceId };

    const url = urljoin(config().workspacesHost, this.version, '/users/invite');
    return api.send('POST', url, inviteUserModel);
  }

  public invitePlatformAdmins(emails: string[]) {
    const inviteUserModel: InviteUserViewModel = { emails, isPlatformAdmin: true };

    const url = urljoin(config().workspacesHost, this.version, '/users/invite');
    return api.send('POST', url, inviteUserModel);
  }

  public getAllUsersForWorkspace(workspaceId: string): Promise<UserViewModel[]> {
    const url = urljoin(config().workspacesHost, this.version, '/users', `?workspaceid=${workspaceId}`);
    return api.send('GET', url);
  }

  public getAllPlatformAdminUsers(): Promise<UserViewModel[]> {
    const url = urljoin(config().workspacesHost, this.version, '/users', `?admin=true`);
    return api.send('GET', url);
  }

  public revokeUser(id: string, workspaceId: string): Promise<void> {
    const removeUserModel: RevokeUserViewModel = {
      userId: id,
      workspaceId,
      isPlatformAdmin: false,
    };

    const url = urljoin(config().workspacesHost, this.version, '/users/revoke');
    return api.send('POST', url, removeUserModel);
  }

  public async validateRegistrationToken(token: string): Promise<ValidateInviteTokenViewModel> {
    const url = urljoin(config().workspacesHost, this.version, '/users/invite/status');
    return await api.send('POST', url, token, { useAuth: false });
  }

  public async registerUser(user: RegisterUserViewModel): Promise<void> {
    const url = urljoin(config().workspacesHost, this.version, '/users/register');
    await api.send('POST', url, user, { useAuth: false });
  }

  public async createUserTokenForConnect(): Promise<string> {
    const url = urljoin(config().workspacesHost, this.version, '/tokens/web-assistant');
    return await api.send('POST', url);
  }

  public async getFeatureFlags(): Promise<FeatureFlagViewModel[]> {
    const url = urljoin(config().workspacesHost, this.version, 'feature-flags', 'workspaces');
    return await api.send('GET', url);
  }
}

export const workspacesApi = new WorkspacesApi();
